// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullAdminRegatta,
  $Model,
  $ModelUpsertParams,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
  TdImage,
} from '@vroskus/library-components';

// Helpers
import {
  cleanFormValues,
  getImageUrl,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
} from '../../../../helpers';
import config from '../../../../config';

// Components
import EmptyTable from '../../../common/EmptyTable';
import RegattaSponsorFields from '../../../fields/RegattaSponsorFields';
import ThQty from '../../../common/ThQty';

// Services
import Services from '../../../../services';

// Types
type $Props = $Translate & {
  id: string;
  item: $FullAdminRegatta;
};

type $State = {
  data: Array<$Model<'RegattaSponsor'>>;
  loading: boolean;
};

class RegattaSponsorsPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      item,
    } = this.props;

    if (isDiff(
      item.id,
      prevProps.item.id,
    )) {
      this.getData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          item,
        } = this.props;

        if (item.id === data.RegattaId && data.modelName === 'RegattaSponsor') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    const RegattaId = item.id;

    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const data = await Services.Api.getManyRegattaSponsor({
          RegattaId,
        });

        await this.setStateAsync({
          data,
          loading: false,
        });
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  renderHeader() {
    const {
      item,
      t,
    } = this.props;

    const RegattaId = item.id;

    const newItem: $ModelUpsertParams<'RegattaSponsor'> = {
      Image: '',
      Link: '',
      Name: '',
      RegattaId,
    };

    return (
      <SectionHeader
        bottomBorder
        iconClass={'icon-wallet'}
        rightButtons={(
          <FormDialog
            fields={(
              <RegattaSponsorFields item={newItem} />
            )}
            id={'create-regatta-sponsor-dialog'}
            labelCloseButton={t('Close')}
            labelSaveButton={t('Save')}
            onAction={(values) => defaultUpsertItem({
              action: async () => {
                const params = cleanFormValues<$Model<'RegattaSponsor'>>({
                  ...newItem,
                  ...values,
                });

                await Services.Api.upsertRegattaSponsor(params);
              },
            })}
            subTitle={t('New')}
            title={t('Regatta sponsor')}
          >
            <Button
              color={'info'}
              id={'create-regatta-sponsor-button'}
              size={'sm'}
              title={t('Add new regatta sponsor')}
            >
              {t('Add new')}
            </Button>
          </FormDialog>
        )}
        title={t('Regatta sponsors')}
      />
    );
  }

  renderButtons(dataItem: $Model<'RegattaSponsor'>) {
    const {
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <FormDialog
          fields={(
            <RegattaSponsorFields item={dataItem} />
          )}
          id={`update-regatta-sponsor-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'RegattaSponsor'>>({
                ...dataItem,
                ...values,
              });

              await Services.Api.upsertRegattaSponsor(params);
            },
          })}
          subTitle={dataItem.Name}
          title={t('Regatta sponsor')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-regatta-sponsor-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-regatta-sponsor-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteRegattaSponsor(dataItem),
          })}
          subTitle={dataItem.Name}
          title={t('Regatta sponsor')}
        >
          <Button
            color={'danger'}
            id={`delete-regatta-sponsor-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th />
            <th>{t('Name')}</th>
            <th>{t('Link')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'RegattaSponsor'>) => (
            <tr key={dataItem.id}>
              <TdImage
                largeImageUrl={getImageUrl({
                  cdnUrl: config.CDN_URL,
                  image: dataItem.Image,
                  size: 'max',
                })}
                noImageUrl={getImageUrl({
                  cdnUrl: config.CDN_URL,
                  image: null,
                  size: 'min',
                })}
                smallImageUrl={getImageUrl({
                  cdnUrl: config.CDN_URL,
                  image: dataItem.Image,
                  size: 'min',
                })}
                value={dataItem.Image}
              />
              <td>{dataItem.Name}</td>
              <td>{dataItem.Link}</td>
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Loader
        loading={loading === true}
        overlay
      >
        {this.renderTable()}
      </Loader>
    );
  }

  render() {
    const {
      id,
    } = this.props;

    return (
      <Card id={id}>
        {this.renderHeader()}
        {this.renderContent()}
      </Card>
    );
  }
}

export default RegattaSponsorsPageView;
